import { Dialog } from '@cimplico/components';
import { ReactNode, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FieldType, WorksheetSchemaField } from '../../../../../types/custom-worksheet-v2';
import { editField } from '../../store/slice';
import { NUMERIC_TYPES } from '../../../../../constants';
import CWFieldInput from '../../../../../components/custom-worksheet/CWFieldInput';

type CalculationOverrideProps = {
    field: WorksheetSchemaField;
    children: ReactNode;
};
function CalculationOverride({ field, children }: CalculationOverrideProps): JSX.Element {
    const [override, setOverride] = useState(false);

    if (field.calculation === null) {
        return <>{children}</>;
    }

    if (override) {
        return (
            <>
                {override && <OverrideDialog field={field} onClose={(): void => setOverride(false)} />}
                {children}
            </>
        );
    }

    return (
        <div
            onClick={(e): void => {
                e.stopPropagation();
                setOverride(true);
            }}
            className="w-full"
        >
            {children}
        </div>
    );
}

type OverrideDialogProps = {
    field: WorksheetSchemaField;
    onClose: () => void;
};

function OverrideDialog({ field, onClose }: OverrideDialogProps): JSX.Element {
    const dispatch = useDispatch();

    const [value, setValue] = useState(field.value);

    function handleSave(): void {
        let _value = value;

        if (_value === '' || _value == null) {
            _value = null;
        }

        if (NUMERIC_TYPES.includes(field.type) && (value === '.' || value === '-')) {
            return;
        }

        // If value entered is greater than the MAX_SAFE_INTEGER, don't save the value
        if (typeof _value === 'number' && _value > Number.MAX_SAFE_INTEGER) {
            return;
        }

        if (_value !== undefined) {
            dispatch(editField({ id: field.id, details: { value: _value } }));
        }
    }

    function handleConfirm(): void {
        handleSave();
        onClose();
    }

    return (
        <Dialog
            description="Overriding this calculation will prevent it from running until the value is cleared."
            isOpen
            primaryAction="Confirm"
            secondaryAction="Cancel"
            title={`Override ${field.name}`}
            onClose={onClose}
            onSecondaryActionClick={onClose}
            onPrimaryActionClick={handleConfirm}
            type="info"
        >
            <div className="pr-8 pb-4">
                <form
                    onSubmit={(e): void => {
                        e.preventDefault();
                        handleConfirm();
                    }}
                >
                    <CWFieldInput fieldType={field.type} defaultValue={field.value} onValueChange={setValue} />
                </form>
            </div>
        </Dialog>
    );
}

export default CalculationOverride;
